/**
 * Completes the og01.ai → Stripe → og01.io landing.
 * Invokes complete-checkout (verify paid → find/create user → OTP for setSession).
 */

import { supabase } from "@/lib/supabaseClient";

export interface CompleteCheckoutProduct {
  stripe_product_id?: string | null;
  stripe_price_id?: string | null;
  tier?: string | null;
  product_type?: string | null;
  name?: string | null;
}

export interface CompleteCheckoutResult {
  paid: boolean;
  reason?: string;
  created_user?: boolean;
  email?: string;
  logged_in_email?: string;
  domain?: string | null;
  stripe_customer_id?: string | null;
  product?: CompleteCheckoutProduct;
  hashed_token?: string;
  email_otp?: string;
  error?: string;
}

async function readErrorBody(error: unknown): Promise<CompleteCheckoutResult> {
  const ctx = (error as { context?: Response })?.context;
  if (ctx && typeof ctx.json === "function") {
    try {
      return (await ctx.json()) as CompleteCheckoutResult;
    } catch {
      /* ignore */
    }
  }
  return { paid: false, reason: "error" };
}

export async function completeCheckout(sessionId: string): Promise<CompleteCheckoutResult> {
  const { data, error } = await supabase.functions.invoke("complete-checkout", {
    body: { session_id: sessionId },
  });

  if (!error) {
    return (data || { paid: false, reason: "error" }) as CompleteCheckoutResult;
  }

  const payload = {
    ...(await readErrorBody(error)),
    ...((data || {}) as CompleteCheckoutResult),
  };

  if (payload.reason === "email_mismatch") return payload;

  return {
    paid: payload.paid === true,
    reason: payload.reason || "error",
    error: error.message,
    email: payload.email,
    logged_in_email: payload.logged_in_email,
    domain: payload.domain,
    stripe_customer_id: payload.stripe_customer_id,
    product: payload.product,
  };
}

export async function establishSessionFromCheckout(result: CompleteCheckoutResult): Promise<{ ok: boolean; error?: string }> {
  if (result.hashed_token) {
    for (const type of ["magiclink", "email"] as const) {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: result.hashed_token,
        type,
      });
      if (!error) return { ok: true };
      if (type === "email" && (!result.email_otp || !result.email)) {
        return { ok: false, error: error.message };
      }
    }
  }

  if (result.email_otp && result.email) {
    const { error } = await supabase.auth.verifyOtp({
      email: result.email,
      token: result.email_otp,
      type: "magiclink",
    });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  }

  return { ok: false, error: "No login token returned" };
}
